/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// Fix: Import FormEvent from React.
import { useState, FormEvent } from 'react';
import { ChatMessage, ChatHistory, UseChatOptions, UseChatReturn } from '../types/chat';
import { streamChatResponse } from '../api/chat';

export const useChat = ({ 
    initialMessages = [], 
    systemInstruction, 
    model = 'gemini-2.5-flash' 
}: UseChatOptions = {}): UseChatReturn => {
    const [messages, setMessages] = useState<ChatHistory>(initialMessages);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fix: Use imported FormEvent type.
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!input.trim()) return;

        setIsLoading(true);
        setError(null);

        const newUserMessage: ChatMessage = { role: 'user', content: input };
        const newMessages: ChatHistory = [...messages, newUserMessage];
        setMessages(newMessages);
        setInput('');

        try {
            // Use the Gateway API router
            const stream = await streamChatResponse(newMessages, systemInstruction, model);

            let fullResponse = '';
            setMessages(prev => [...prev, { role: 'model', content: '' }]);

            for await (const chunk of stream) {
                // Unified stream format expected { text: string }
                const text = chunk.text;
                if (text) {
                    fullResponse += text;
                    setMessages(prev => {
                        const lastMessage = prev[prev.length - 1];
                        if (lastMessage.role === 'model') {
                            lastMessage.content = fullResponse;
                            return [...prev.slice(0, -1), lastMessage];
                        }
                        return prev;
                    });
                }
            }

        } catch (err: any) {
            console.error("Chat Error:", err);
            let errorMessage = "Failed to get response. Please check your connection or API configuration.";
            if (err.message && err.message.includes("API_KEY")) {
                errorMessage = "The AI Assistant is not configured. Please contact support.";
            }
            setError(errorMessage);
            // Add error message to chat
            setMessages(prev => [...prev, { role: 'model', content: errorMessage }]);
        } finally {
            setIsLoading(false);
        }
    };

    const reset = () => {
        setMessages(initialMessages);
        setInput('');
        setIsLoading(false);
        setError(null);
    };

    return {
        messages,
        input,
        setInput,
        handleSubmit,
        isLoading,
        error,
        reset,
        setMessages // Exposed for manual context injection
    };
};